import { useState, useRef } from "react";
import { motion } from "framer-motion";
import useApi from "../../../services/useApi";
import { useDispatch } from "react-redux";
import { showToast } from "../../../components/toastMessage/toastSlice";
import useTranslation from "../../../components/CustomHooks/useTranslation";
import { getFirefoxCssFix } from "../../../components/commonfunctions/getFirefoxCssFix";
import Image from "../../../components/images/Image";
import Button from "../../../components/buttons/Button";
import InputLabel from "../../../components/inputfields/InputLabel";
import ErrorMessage from "../../../components/ErrorMessages/ErrorMessage";

interface UpdatePinModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PIN_LENGTH = 4;

function PinInputField({ label, value, setValue, errorMessage, required, setError, errorKey }: {
  label: string;
  value: string[];
  setValue: (val: string[]) => void;
  errorMessage?: string;
  required?: boolean;
  setError: (cb: (prev: { [key: string]: string }) => { [key: string]: string }) => void;
  errorKey: string;
}) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (val: string, idx: number) => {
    const numericValue = val.replace(/[^0-9]/g, "");
    if (numericValue.length > 1) return;
    const updated = [...value];
    updated[idx] = numericValue;
    setValue(updated);
    setError(prev => ({ ...prev, [errorKey]: "" }));
    if (numericValue && idx < value.length - 1) {
      inputRefs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    idx: number
  ) => {
    if (e.key === "ArrowLeft" && idx > 0) {
      const prevInput = inputRefs.current[idx - 1];
      if (prevInput) {
        prevInput.focus();
        // Set cursor position to end of the field if it has a value
        setTimeout(() => {
          if (value[idx - 1]) {
            prevInput.setSelectionRange(1, 1);
          }
        }, 0);
      }
    }
    if (e.key === "ArrowRight" && idx < value.length - 1) {
      const nextInput = inputRefs.current[idx + 1];
      if (nextInput) {
        nextInput.focus();
        // Set cursor position to end of the field if it has a value
        setTimeout(() => {
          if (value[idx + 1]) {
            nextInput.setSelectionRange(1, 1);
          }
        }, 0);
      }
    }
    if (e.key === "Backspace") {
      if (!value[idx] && idx > 0) {
        const prevInput = inputRefs.current[idx - 1];
        if (prevInput) {
          prevInput.focus();
          // Set cursor position to end of the field if it has a value
          setTimeout(() => {
            if (value[idx - 1]) {
              prevInput.setSelectionRange(1, 1);
            }
          }, 0);
        }
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData
      .getData("text")
      .replace(/[^0-9]/g, "")
      .slice(0, value.length);
    const updated = [...value];
    pasteData.split("").forEach((digit, i) => {
      if (i < value.length) {
        updated[i] = digit;
      }
    });
    setValue(updated);
    setError(prev => ({ ...prev, [errorKey]: "" }));
    const nextIndex = pasteData.length >= value.length ? value.length - 1 : pasteData.length;
    inputRefs.current[nextIndex]?.focus();
  };

  return (
    <div className={errorMessage ? "mb-[6px] max-w-[264px]" : "mb-[32px] max-w-[264px]"}>
      <InputLabel labelName={label} id={label} required={required} />
      <div className="flex gap-4 ">
        {value.map((digit, idx) => (
          <input
            key={idx}
            ref={el => (inputRefs.current[idx] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            autoComplete="off"
            value={digit ? '●' : ''}
            onChange={e => handleChange(e.target.value, idx)}
            onKeyDown={e => handleKeyDown(e, idx)}
            onPaste={handlePaste}
            placeholder="*"
            className={`w-[54px] h-14 text-center text-lg border rounded ${
              errorMessage ? "border-negative" : "border-P_light_active focus:border-2"
            } focus:outline-none placeholder-gray-400 transition-colors`}
          />
        ))}
      </div>
      {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
    </div>
  );
}

export default function UpdatePinModal({ isOpen, onClose }: UpdatePinModalProps) {
  const InitialState = {
    current_pin: Array(PIN_LENGTH).fill("") as string[],
    new_pin: Array(PIN_LENGTH).fill("") as string[],
    confirm_pin: Array(PIN_LENGTH).fill("") as string[],
  };
  const [state, setState] = useState(InitialState);
  const [error, setError] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const { PatchAPI } = useApi();
  const dispatch = useDispatch();
  const { translate } = useTranslation({});

  const handleSubmit = async () => {
    setError({});
    const mandatoryFields = ["current_pin", "new_pin", "confirm_pin"];
    // Check for empty fields or incomplete PINs
    const err: { [key: string]: string } = {};
    mandatoryFields.forEach((field) => {
      if (state[field as keyof typeof state].join("") === "" || state[field as keyof typeof state].some(d => d === "") || state[field as keyof typeof state].length !== 4) {
        err[field] = "This field is required";
      }
    });
    if (Object.keys(err).length > 0) {
      setError(err);
      return;
    }
    if (state.new_pin.join("") !== state.confirm_pin.join("")) {
      setError({ confirm_pin: "PINs do not match" });
      return;
    }
    if (state.new_pin.join("") === state.current_pin.join("")) {
      setError({ new_pin: "New PIN cannot be the same as Current PIN" });
      return;
    }
    setLoading(true);
    let body = {
      current_pin: state.current_pin.join(""),
      pin: state.new_pin.join(""),
      confirm_pin: state.confirm_pin.join(""),
    };
    let response;
    try {
      response = await PatchAPI(
        "admins/update-pin",
        body
      );
    } catch (err) {
      response = { error: true, data: { message: "Failed to update PIN" } };
    }
    if( response.data.status === 400 && response.data.response.data.message.includes("Invalid PIN")) {
        setError({ current_pin: response.data.response.data.message });
    } else if (!response.error) {
      dispatch(
        showToast({
          type: "success",
          message: translate("PIN updated successfully"),
        })
      );
      onClose();
    } else {
      dispatch(
        showToast({
          type: "error",
          message: response?.data?.message || "Something went wrong",
        })
      );
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="relative bg-white flex flex-col justify-center items-center rounded-lg shadow-lg px-20 py-10 mx-auto">
        <Image
          src="close"
          onClick={onClose}
          className="absolute top-6 right-6 cursor-pointer text-xl font-bold z-10"
        />
        <div className="flex flex-col items-center justify-center max-w-[330px] mb-5 mt-3">
          <h2 className="text-2xl font-semibold mb-4">
            {translate("Update PIN")}
          </h2>
          <p className="text-secondary_text break-all w-[320px] text-center">
            Please enter your current PIN and set a new one to update your credentials.
          </p>
        </div>
        <div className="flex flex-col max-w-[264px] items-center justify-center">
          <PinInputField
            label="Current PIN"
            value={state.current_pin}
            setValue={val => setState(prev => ({ ...prev, current_pin: val }))}
            errorMessage={error.current_pin}
            required={true}
            setError={setError}
            errorKey="current_pin"
          />
          <PinInputField
            label="New PIN"
            value={state.new_pin}
            setValue={val => setState(prev => ({ ...prev, new_pin: val }))}
            errorMessage={error.new_pin}
            required={true}
            setError={setError}
            errorKey="new_pin"
          />
          <PinInputField
            label="Confirm PIN"
            value={state.confirm_pin}
            setValue={val => setState(prev => ({ ...prev, confirm_pin: val }))}
            errorMessage={error.confirm_pin}
            required={true}
            setError={setError}
            errorKey="confirm_pin"
          />
          <Button
            buttonOnclick={handleSubmit}
            buttonName="Update"
            className="mt-3 w-[264px] h-[56px] bg-p_primary text-white py-2 rounded-lg font-semibold disabled:opacity-60"
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
} 